import { findScrollableParent } from '@baserow/modules/core/utils/dom'

/**
 * This directive can by used to enable vertical drag and drop sorting of an array of
 * items. When the dragging starts, it simply shows a target position and will call a
 * function when the item has been dropped. It will not actually change the order of
 * the items, but it will only show the drag and drop effect and calculates the new
 * order of the items. The actual updating has to happen in the update function.
 *
 * Optionally a handle selector can be provided by doing
 * `v-sortable="{ id: item.id, update: order, handle: '.child-element' }"`.
 *
 * Example:
 *
 * ```
 * <div
 *   v-for="item in items"
 *   :key="item.id"
 *   v-sortable="{ id: item.id, update: onUpdate }"
 * ></div>
 *
 * export default {
 *   data() {
 *     return {
 *       items: [{'id': 25, order: 1}, {'id': 27, order: 2}, {'id': 30, order: 3}]
 *     }
 *   },
 *   methods: {
 *     onUpdate(itemIds) {
 *       console.log(itemIds) // [25, 27, 30]
 *     },
 *   },
 * }
 * ```
 *
 * By default a list is only sortable within its own parent: `up()` reads the
 * dropped-into order from the origin parent's own children, so nothing can be
 * dropped into a different list. Opting into cross-list drops needs two more
 * options: `containerId` (this list's own identity) and `onMoveToContainer`
 * (called instead of `update` when the item is dropped into a *different*
 * list). Both the origin and every drop-target list need `containerId` set,
 * and each list's root element needs a matching `data-sortable-container-id`
 * attribute so the directive can find it under the cursor:
 *
 * ```
 * <ul data-sortable-container-id="a">
 *   <li v-sortable="{ id, update, containerId: 'a', onMoveToContainer }"></li>
 * </ul>
 * <ul data-sortable-container-id="b">
 *   <li v-sortable="{ id, update, containerId: 'b', onMoveToContainer }"></li>
 * </ul>
 * ```
 *
 * `onMoveToContainer(itemId, targetContainerId, beforeId)` receives the
 * dragged item's id, the `data-sortable-container-id` of the list it was
 * dropped into, and the id of the item it was dropped before (`null` for the
 * end of the list). It is the caller's job to move the item and, if the
 * target list needs a specific position rather than just membership, order
 * it -- the directive only reports where the drop happened.
 *
 * Consumers that never set `containerId` are unaffected: `up()` falls back to
 * the original single-list `update` call exactly as before.
 */
let parent
let scrollableParent
let indicator
// The list currently under the cursor, once a drag has crossed into a
// different `data-sortable-container-id` list than the one it started in.
// `null` whenever the cursor is back over the origin list (the common case),
// so cross-list consumers pay no extra cost for opting in until a drag
// actually leaves the origin.
let currentContainer
let currentContainerId

export default {
  /**
   * Called when the directive must bind to the element. It will register the
   * mousedown event on the element, which is used to start the drag and drop
   * process.
   */
  beforeMount(el, binding) {
    binding.dir.updated(el, binding)
    el.sortableAutoScrolling = false

    el.mousedownEvent = (event) => {
      if (!el.sortableEnabled || event.button !== 0) {
        return
      }

      if (binding.value.handle) {
        const handle = el.querySelector(binding.value.handle)
        if (!handle || !handle.contains(event.target)) {
          return
        }
      }

      el.sortableMoved = false
      el.sortableStartClientX = event.clientX
      el.sortableStartClientY = event.clientY

      el.mouseMoveEvent = (event) => binding.dir.move(el, binding, event)
      window.addEventListener('mousemove', el.mouseMoveEvent)

      el.mouseUpEvent = (event) => binding.dir.up(el, binding, event)
      window.addEventListener('mouseup', el.mouseUpEvent)

      el.keydownEvent = (event) => {
        if (event.key === 'Escape') {
          // When the user presses the escape key we want to cancel the action
          binding.dir.cancel(el, event)
        }
      }
      document.body.addEventListener('keydown', el.keydownEvent)

      parent = el.parentNode
      scrollableParent = findScrollableParent(parent) || parent
      currentContainer = null
      currentContainerId = null

      // If the parent container is not positioned, add the position automatically.
      if (getComputedStyle(parent).position === 'static') {
        parent.style.position = 'relative'
      }

      // Prevent the browser from starting a text selection.
      event.preventDefault()

      indicator = document.createElement('div')
      indicator.classList.add('sortable-position-indicator')
      parent.insertBefore(indicator, parent.firstChild)
    }
    el.addEventListener('mousedown', el.mousedownEvent)
  },
  /**
   * When the directive must unbind from the element, we will remove all the events
   * that could have been added.
   */
  unmounted(el, binding) {
    if (el.sortableMoved) {
      binding.dir.cancel(el)
    }

    el.removeEventListener('mousedown', el.mousedownEvent)
  },
  updated(el, binding) {
    el.sortableId = binding.value.id
    el.sortableEnabled =
      binding.value.enabled || binding.value.enabled === undefined
    el.sortableMarginLeft = binding.value.marginLeft
    el.sortableMarginRight = binding.value.marginRight
    el.sortableMarginTop = binding.value.marginTop
    el.sortableMarginTopLast = binding.value.marginTopLast
    el.sortableContainerId = binding.value.containerId
    el.sortableOnMoveToContainer = binding.value.onMoveToContainer
  },
  /**
   * Called when the user moves the mouse when the dragging of the element has
   * started. It will calculate the target indicator position and saves before which
   * element it must be placed.
   */
  move(el, binding, event = null, startAutoScroll = true) {
    if (event !== null) {
      event.preventDefault()
      el.sortableLastMoveEvent = event
    } else {
      event = el.sortableLastMoveEvent
    }

    // Sometimes the user could accidentally drag the element one or two pixels while
    // clicking it. Because it could be annoying that the click doesn't work because
    // the moving state started, we check here if the user has at least dragged
    // the element 3 pixels vertically or horizontally before starting the moved state.
    if (!el.sortableMoved) {
      if (
        Math.abs(event.clientX - el.sortableStartClientX) > 3 ||
        Math.abs(event.clientY - el.sortableStartClientY) > 3
      ) {
        el.sortableMoved = true
      } else {
        return
      }
    }

    // Cross-list drop is opt-in: only look for a different container under
    // the cursor if this drag's own list declared a `containerId`. Without
    // it, `activeParent` below just stays `parent` and everything behaves
    // exactly as it did before this feature existed.
    let activeParent = parent
    if (el.sortableContainerId !== undefined) {
      const hovered = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest('[data-sortable-container-id]')
      const hoveredIsOwnList = hovered === parent
      const hoveredIsDifferentContainer =
        hovered &&
        !hoveredIsOwnList &&
        hovered.dataset.sortableContainerId !== el.sortableContainerId

      if (hoveredIsDifferentContainer && hovered !== currentContainer) {
        currentContainer = hovered
        currentContainerId = hovered.dataset.sortableContainerId
        // Re-parent the indicator into the newly-hovered list so the
        // existing hit-testing/positioning math below keeps working
        // unmodified -- it always measures against whichever element the
        // indicator currently lives in.
        if (getComputedStyle(hovered).position === 'static') {
          hovered.style.position = 'relative'
        }
        hovered.insertBefore(indicator, hovered.firstChild)
      } else if (hoveredIsOwnList && currentContainer !== null) {
        currentContainer = null
        currentContainerId = null
        parent.insertBefore(indicator, parent.firstChild)
      }

      if (currentContainer !== null) {
        activeParent = currentContainer
      }
    }

    // Set pointer events to none because that will prevent hover and click
    // effects.
    const all = [...activeParent.childNodes].filter(
      (e) => e !== indicator && e.nodeType === 1 && e.sortableId !== undefined
    )

    // Add the `sortable-sorting-item` which disables the pointer events and user
    // select of all the sortable items. This will give a smoother user experience
    // as the user can't accidentally click the item and can't select the text while
    // dragging.
    all.forEach((s) => {
      s.classList.add('sortable-sorting-item')
    })

    const parentRect = activeParent.getBoundingClientRect()

    // Using the mouse position and the position of the items we can calculate
    // before which item the dragging item must be placed. If the position of the
    // mouse is above the vertical center of the element, it must be placed before
    // that item.
    let before = null
    let beforeRect = {}
    for (let i = 0; i < all.length; i++) {
      beforeRect = all[i].getBoundingClientRect()
      if (event.clientY < beforeRect.top + beforeRect.height / 2) {
        before = all[i]
        break
      }
    }

    // Save the element where the dragging item must be placed before so that the
    // new order can be calculated when the user releases the mouse.
    el.sortableBeforeElement = before

    // Calculate the target indicator position based on the position of the
    // beforeElement. If the beforeElement is null, it means that the dragging
    // element must be moved to the end -- or, if the list has no items at all
    // (an empty cross-list drop target), to its one and only position.
    const elementRect = el.getBoundingClientRect()
    const afterRect =
      all.length > 0 ? all[all.length - 1].getBoundingClientRect() : parentRect
    // Allow callers to nudge the indicator differently for the trailing
    // position (when the dragged element will land at the very end of the
    // list) compared to between-items positions, since there is no natural
    // gap below the last item to centre the indicator in.
    const verticalMargin = before
      ? el.sortableMarginTop || 0
      : (el.sortableMarginTopLast ?? el.sortableMarginTop ?? 0)
    const top =
      (before
        ? beforeRect.top - indicator.clientHeight / 2
        : all.length > 0
          ? afterRect.top + afterRect.height
          : afterRect.top) -
      parentRect.top +
      activeParent.scrollTop +
      verticalMargin
    const left = elementRect.left - parentRect.left
    indicator.style.left = left + (el.sortableMarginLeft || 0) + 'px'
    indicator.style.width =
      elementRect.width -
      (el.sortableMarginLeft || 0) -
      (el.sortableMarginRight || 0) +
      'px'
    indicator.style.top = top + 'px'

    // If the user is not already auto scrolling, which happens while dragging and
    // moving the element close to the end of the view port at the top or bottom
    // side, we might need to initiate that process.
    if (
      scrollableParent.scrollHeight > scrollableParent.clientHeight &&
      (!el.sortableAutoScrolling || !startAutoScroll)
    ) {
      const scrollableParentRect = scrollableParent.getBoundingClientRect()
      const parentHeight =
        scrollableParentRect.bottom - scrollableParentRect.top
      const side = Math.ceil((parentHeight / 100) * 10)
      const autoScrollMouseTop = event.clientY - scrollableParentRect.top
      const autoScrollMouseBottom = parentHeight - autoScrollMouseTop
      let speed = 0

      if (autoScrollMouseTop < side) {
        speed = -(3 - Math.ceil((Math.max(0, autoScrollMouseTop) / side) * 3))
      } else if (autoScrollMouseBottom < side) {
        speed = 3 - Math.ceil((Math.max(0, autoScrollMouseBottom) / side) * 3)
      }

      // If the speed is either a positive or negative, so not 0, we know that we
      // need to start auto scrolling.
      if (speed !== 0) {
        el.sortableAutoScrolling = true
        scrollableParent.scrollTop += speed
        el.sortableScrollTimeout = setTimeout(() => {
          binding.dir.move(el, binding, null, false)
        }, 10)
      } else {
        el.sortableAutoScrolling = false
      }
    }
  },
  /**
   * Called when the user releases the mouse after the dragging of the element has
   * started. It will check calculate the new order of all items based on the last
   * beforeElement element saved by the move method. If the item has changed
   * position, the update function is called which needs to change the actual order
   * of the items.
   */
  up(el, binding) {
    // Read before `cancel()` resets it: a non-null `currentContainer` means
    // the drop happened over a different list than the one the drag started
    // in.
    const droppedIntoContainerId =
      currentContainer !== null ? currentContainerId : null

    binding.dir.cancel(el, binding)

    if (!el.sortableMoved) {
      return
    }

    el.sortableMoved = false

    // It could be that the element or a child element has a click handler. When the
    // user releases the mouse pointer, that click event could also be triggered
    // which we don't because we are dragging the element instead of clicking on it
    // directly. This makes sure that when releasing the mouse pointer, that click
    // event is stopped.
    const preventOtherClickEvent = (event) => {
      event.stopPropagation()
      window.removeEventListener('click', preventOtherClickEvent, true)
    }
    window.addEventListener('click', preventOtherClickEvent, true)
    // Remove the event because it could be that the user wants to click on the
    // element right after it has been moved.
    setTimeout(() => {
      window.removeEventListener('click', preventOtherClickEvent, true)
    })

    const beforeId = el.sortableBeforeElement?.sortableId ?? null

    // Dropped into a different list than it started in: this is a move, not
    // a reorder. The origin list's order is untouched, so there is no
    // `oldOrder`/`newOrder` to compute -- the caller decides what membership
    // in the target list means and, if it cares about position, orders it
    // itself using `beforeId`.
    if (droppedIntoContainerId !== null) {
      el.sortableOnMoveToContainer?.(
        el.sortableId,
        droppedIntoContainerId,
        beforeId
      )
      return
    }

    const oldOrder = [...parent.childNodes]
      .filter((e) => e.nodeType === 1 && e.sortableId !== undefined)
      .map((e) => e.sortableId)
    const newOrder = oldOrder.filter((id) => id !== el.sortableId)
    const targetIndex = el.sortableBeforeElement
      ? newOrder.findIndex((id) => id === el.sortableBeforeElement.sortableId)
      : newOrder.length

    if (targetIndex === -1) {
      return
    }

    newOrder.splice(targetIndex, 0, el.sortableId)

    if (JSON.stringify(oldOrder) === JSON.stringify(newOrder)) {
      return
    }

    binding.value.update(newOrder, oldOrder, el.sortableId, beforeId)
  },
  /**
   * Cancels the sorting by removing the target indicator, sorting classes and event
   * listeners.
   */
  cancel(el) {
    clearTimeout(el.sortableScrollTimeout)

    if (indicator.parentNode) {
      indicator.parentNode.removeChild(indicator)
    }

    // The drag may have crossed into a different container than it started
    // in, which also had `sortable-sorting-item` applied to its children in
    // `move()` -- both need the class stripped, not just the origin.
    const containers =
      currentContainer !== null ? [parent, currentContainer] : [parent]
    containers.forEach((container) => {
      ;[...container.childNodes]
        .filter((e) => e.nodeType === 1)
        .forEach((s) => s.classList.remove('sortable-sorting-item'))
    })
    currentContainer = null
    currentContainerId = null

    window.removeEventListener('mouseup', el.mouseUpEvent)
    window.removeEventListener('mousemove', el.mouseMoveEvent)
    document.body.removeEventListener('keydown', el.keydownEvent)
  },
}
